'use client';

import { useState, useEffect, useRef, useMemo, Fragment } from 'react';
import type { Locale } from '@/lib/content';
import type { ToolContent } from '@/lib/tools/types';
import { Package, Copy, Check, RotateCcw, ArrowRight, Settings2 } from 'lucide-react';
import Link from 'next/link';
import { logToolUsage } from '@/lib/tools/analytics';
import { ToolSignupWall } from './tool-signup-wall';
import {
  BOL_SHIPPING_TIERS,
  BOL_COMMISSION_CATEGORIES,
  BOL_BE_SURCHARGE,
  BOL_Q4_SURCHARGE_FACTOR,
  BOL_VAT_RATE,
  BOL_DATA_UPDATED,
} from '@/lib/tools/marketplace-data';

interface BolCalculatorProps {
  locale: Locale;
  content: ToolContent;
}

export function BolCalculator({ locale, content }: BolCalculatorProps) {
  const [packageTier, setPackageTier] = useState('brievenbuspakket');
  const [destination, setDestination] = useState<'nl' | 'be'>('nl');
  const [period, setPeriod] = useState<'normal' | 'q4'>('normal');
  const [monthlyVolume, setMonthlyVolume] = useState('100');
  const [includeVat, setIncludeVat] = useState(false);
  const [advancedMode, setAdvancedMode] = useState(false);
  const [sellingPrice, setSellingPrice] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [commissionCategory, setCommissionCategory] = useState('computers-tablets');
  const [shippingMethod, setShippingMethod] = useState<'vvb' | 'eigen'>('vvb');
  const [ownShippingCost, setOwnShippingCost] = useState('');
  const [copied, setCopied] = useState<string | null>(null);
  const hasLoggedRef = useRef(false);

  const labels = content.labels;

  useEffect(() => {
    const vol = parseInt(monthlyVolume);
    const sell = parseFloat(sellingPrice.replace(',', '.'));
    if (!hasLoggedRef.current && ((!isNaN(vol) && vol > 0) || (!isNaN(sell) && sell > 0))) {
      hasLoggedRef.current = true;
      logToolUsage({ tool_name: 'bol-calculator', locale });
    }
  }, [sellingPrice, monthlyVolume, locale]);

  const selectedTier = BOL_SHIPPING_TIERS.find(t => t.id === packageTier) ?? BOL_SHIPPING_TIERS[0];
  const selectedCategory = BOL_COMMISSION_CATEGORIES.find(c => c.id === commissionCategory) ?? BOL_COMMISSION_CATEGORIES[0];

  const groupedCategories = useMemo(() => {
    const groups: Record<string, typeof BOL_COMMISSION_CATEGORIES> = {};
    for (const cat of BOL_COMMISSION_CATEGORIES) {
      if (!groups[cat.group]) groups[cat.group] = [];
      groups[cat.group].push(cat);
    }
    return groups;
  }, []);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(locale === 'nl' ? 'nl-NL' : locale === 'de' ? 'de-DE' : locale === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  };

  const copyToClipboard = async (value: string, key: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const calculateShipping = () => {
    let shippingBase = shippingMethod === 'vvb' ? selectedTier.vvbCost : (parseFloat(ownShippingCost.replace(',', '.')) || 0);
    let fixedContribution = selectedTier.fixedContribution;

    if (destination === 'be') {
      shippingBase += BOL_BE_SURCHARGE;
    }
    if (period === 'q4') {
      fixedContribution *= BOL_Q4_SURCHARGE_FACTOR;
    }

    return { shippingBase, fixedContribution, total: shippingBase + fixedContribution };
  };

  const calculateResults = () => {
    const volume = parseInt(monthlyVolume) || 0;
    const shipping = calculateShipping();
    const applyVat = (amount: number) => includeVat ? amount * (1 + BOL_VAT_RATE) : amount;

    if (advancedMode) {
      const selling = parseFloat(sellingPrice.replace(',', '.')) || 0;
      const purchasing = parseFloat(purchasePrice.replace(',', '.')) || 0;
      if (selling <= 0) return null;

      const commission = selling * selectedCategory.rate;
      const totalCostPerUnit = commission + shipping.total;
      const profitPerUnit = selling - purchasing - totalCostPerUnit;
      const margin = (profitPerUnit / selling) * 100;

      return {
        mode: 'advanced' as const,
        commission: applyVat(commission),
        commissionRate: selectedCategory.rate,
        shippingBase: applyVat(shipping.shippingBase),
        fixedContribution: applyVat(shipping.fixedContribution),
        shippingTotal: applyVat(shipping.total),
        totalCostPerUnit: applyVat(totalCostPerUnit),
        profitPerUnit: applyVat(profitPerUnit),
        margin,
        monthlyProfit: applyVat(profitPerUnit) * volume,
        yearlyProfit: applyVat(profitPerUnit) * volume * 12,
        monthlyRevenue: applyVat(selling) * volume,
        monthlyCost: applyVat(totalCostPerUnit) * volume,
      };
    }

    return {
      mode: 'simple' as const,
      shippingBase: applyVat(shipping.shippingBase),
      fixedContribution: applyVat(shipping.fixedContribution),
      shippingPerUnit: applyVat(shipping.total),
      monthlyShipping: applyVat(shipping.total * volume),
      yearlyShipping: applyVat(shipping.total * volume * 12),
    };
  };

  const result = calculateResults();

  const reset = () => {
    setPackageTier('brievenbuspakket');
    setDestination('nl');
    setPeriod('normal');
    setMonthlyVolume('100');
    setIncludeVat(false);
    setAdvancedMode(false);
    setSellingPrice('');
    setPurchasePrice('');
    setCommissionCategory('computers-tablets');
    setShippingMethod('vvb');
    setOwnShippingCost('');
  };

  return (
    <div className="mx-auto max-w-2xl">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-100">
          <Package className="h-7 w-7 text-blue-700" />
        </div>
        <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          {content.title}
        </h2>
        <p className="mt-3 text-lg text-slate-600">{content.description}</p>
      </div>

      {/* Calculator Card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        {/* Mode Toggle */}
        <div className="mb-6 flex items-center justify-between">
          <span className="text-sm font-medium text-slate-700">{labels.advancedMode || 'Geavanceerde modus'}</span>
          <button
            type="button"
            onClick={() => setAdvancedMode(!advancedMode)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${advancedMode ? 'bg-brand-600' : 'bg-slate-300'}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${advancedMode ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        {/* Package Tier */}
        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-slate-700">{labels.packageType || 'Pakkettype'}</label>
          <div className="grid grid-cols-2 gap-2">
            {BOL_SHIPPING_TIERS.map((tier) => (
              <button
                key={tier.id}
                type="button"
                onClick={() => setPackageTier(tier.id)}
                className={`rounded-xl px-3 py-3 text-left transition-all ${
                  packageTier === tier.id
                    ? 'bg-brand-600 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <span className="block text-sm font-medium">{labels[`tier_${tier.id}`] || tier.id}</span>
                <span className={`block text-xs mt-0.5 ${packageTier === tier.id ? 'text-brand-100' : 'text-slate-500'}`}>
                  {tier.dimensions}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Destination */}
        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-slate-700">{labels.destination || 'Bestemming'}</label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setDestination('nl')}
              className={`rounded-xl px-4 py-3 text-sm font-medium transition-all ${
                destination === 'nl' ? 'bg-brand-600 text-white shadow-sm' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {labels.destinationNL || 'Nederland'}
            </button>
            <button
              type="button"
              onClick={() => setDestination('be')}
              className={`rounded-xl px-4 py-3 text-sm font-medium transition-all ${
                destination === 'be' ? 'bg-brand-600 text-white shadow-sm' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {labels.destinationBE || 'België (+€2,50)'}
            </button>
          </div>
        </div>

        {/* Period */}
        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-slate-700">{labels.period || 'Periode'}</label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setPeriod('normal')}
              className={`rounded-xl px-4 py-3 text-sm font-medium transition-all ${
                period === 'normal' ? 'bg-brand-600 text-white shadow-sm' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {labels.periodNormal || 'Normaal'}
            </button>
            <button
              type="button"
              onClick={() => setPeriod('q4')}
              className={`rounded-xl px-4 py-3 text-sm font-medium transition-all ${
                period === 'q4' ? 'bg-brand-600 text-white shadow-sm' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {labels.periodQ4 || 'Q4 Piek (+40%)'}
            </button>
          </div>
        </div>

        {/* Monthly Volume */}
        <div className="mb-6">
          <label htmlFor="monthlyVolume" className="mb-2 block text-sm font-medium text-slate-700">
            {labels.monthlyVolume || 'Maandelijks volume'}
          </label>
          <input
            type="number"
            id="monthlyVolume"
            value={monthlyVolume}
            onChange={(e) => setMonthlyVolume(e.target.value)}
            min="0"
            placeholder="100"
            className="block w-full rounded-xl border border-slate-300 py-3 px-4 text-lg focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
        </div>

        {/* Advanced Mode Fields */}
        {advancedMode && (
          <div className="mb-6 space-y-4 rounded-xl border border-blue-100 bg-blue-50/50 p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-blue-700">
              <Settings2 className="h-4 w-4" />
              {labels.profitAnalysis || 'Winstanalyse'}
            </div>

            {/* Selling Price */}
            <div>
              <label htmlFor="sellingPrice" className="mb-1 block text-sm font-medium text-slate-700">
                {labels.sellingPrice || 'Verkoopprijs'}
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-slate-500">€</span>
                <input
                  type="text"
                  id="sellingPrice"
                  value={sellingPrice}
                  onChange={(e) => setSellingPrice(e.target.value)}
                  placeholder="0,00"
                  className="block w-full rounded-xl border border-slate-300 py-2.5 pl-8 pr-4 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                />
              </div>
            </div>

            {/* Purchase Price */}
            <div>
              <label htmlFor="purchasePrice" className="mb-1 block text-sm font-medium text-slate-700">
                {labels.purchasePrice || 'Inkoopprijs'}
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-slate-500">€</span>
                <input
                  type="text"
                  id="purchasePrice"
                  value={purchasePrice}
                  onChange={(e) => setPurchasePrice(e.target.value)}
                  placeholder="0,00"
                  className="block w-full rounded-xl border border-slate-300 py-2.5 pl-8 pr-4 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                />
              </div>
            </div>

            {/* Commission Category */}
            <div>
              <label htmlFor="commissionCategory" className="mb-1 block text-sm font-medium text-slate-700">
                {labels.commissionCategory || 'Commissiecategorie'}
              </label>
              <select
                id="commissionCategory"
                value={commissionCategory}
                onChange={(e) => setCommissionCategory(e.target.value)}
                className="block w-full rounded-xl border border-slate-300 py-2.5 px-4 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
              >
                {Object.entries(groupedCategories).map(([group, categories]) => (
                  <optgroup key={group} label={group}>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.label} ({(cat.rate * 100).toFixed(0)}%)
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            {/* Shipping Method */}
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                {labels.shippingMethod || 'Verzendmethode'}
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setShippingMethod('vvb')}
                  className={`rounded-xl px-3 py-2.5 text-sm font-medium transition-all ${
                    shippingMethod === 'vvb' ? 'bg-brand-600 text-white shadow-sm' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {labels.vvb || 'Verzonden via bol (VVB)'}
                </button>
                <button
                  type="button"
                  onClick={() => setShippingMethod('eigen')}
                  className={`rounded-xl px-3 py-2.5 text-sm font-medium transition-all ${
                    shippingMethod === 'eigen' ? 'bg-brand-600 text-white shadow-sm' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {labels.ownShipping || 'Eigen verzending'}
                </button>
              </div>
            </div>

            {/* Own Shipping Cost (only if eigen) */}
            {shippingMethod === 'eigen' && (
              <div>
                <label htmlFor="ownShippingCost" className="mb-1 block text-sm font-medium text-slate-700">
                  {labels.ownShippingCost || 'Eigen verzendkosten'}
                </label>
                <div className="relative">
                  <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-slate-500">€</span>
                  <input
                    type="text"
                    id="ownShippingCost"
                    value={ownShippingCost}
                    onChange={(e) => setOwnShippingCost(e.target.value)}
                    placeholder="0,00"
                    className="block w-full rounded-xl border border-slate-300 py-2.5 pl-8 pr-4 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* BTW Toggle */}
        <div className="mb-6 flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
          <span className="text-sm font-medium text-slate-700">{labels.includeVat || 'Inclusief BTW (21%)'}</span>
          <button
            type="button"
            onClick={() => setIncludeVat(!includeVat)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${includeVat ? 'bg-brand-600' : 'bg-slate-300'}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${includeVat ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        {/* Results */}
        {result && result.mode === 'simple' && (
          <div className="mt-6 space-y-3 rounded-xl bg-slate-50 p-4">
            <ResultRow label={labels.shippingCost || 'Verzendkosten'} value={formatCurrency(result.shippingBase)} copyKey="shippingBase" copied={copied} onCopy={copyToClipboard} copyLabel={labels.copyResult} />
            <ResultRow label={labels.fixedContribution || 'Vaste verzendbijdrage'} value={formatCurrency(result.fixedContribution)} copyKey="fixedContribution" copied={copied} onCopy={copyToClipboard} copyLabel={labels.copyResult} />
            <div className="border-t border-slate-200 pt-3">
              <ResultRow label={labels.perUnit || 'Per pakket'} value={formatCurrency(result.shippingPerUnit)} copyKey="perUnit" copied={copied} onCopy={copyToClipboard} bold copyLabel={labels.copyResult} />
            </div>
            <ResultRow label={labels.perMonth || 'Per maand'} value={formatCurrency(result.monthlyShipping)} copyKey="perMonth" copied={copied} onCopy={copyToClipboard} copyLabel={labels.copyResult} />
            <div className="border-t border-slate-200 pt-3">
              <ResultRow label={labels.perYear || 'Per jaar'} value={formatCurrency(result.yearlyShipping)} copyKey="perYear" copied={copied} onCopy={copyToClipboard} bold copyLabel={labels.copyResult} />
            </div>
          </div>
        )}

        {result && result.mode === 'advanced' && (
          <div className="mt-6 space-y-3 rounded-xl bg-slate-50 p-4">
            <ResultRow label={`${labels.commission || 'Commissie'} (${(result.commissionRate * 100).toFixed(0)}%)`} value={formatCurrency(result.commission)} copyKey="commission" copied={copied} onCopy={copyToClipboard} copyLabel={labels.copyResult} />
            <ResultRow label={labels.shippingTotal || 'Verzendkosten totaal'} value={formatCurrency(result.shippingTotal)} copyKey="shippingTotal" copied={copied} onCopy={copyToClipboard} copyLabel={labels.copyResult} />
            <div className="border-t border-slate-200 pt-3">
              <ResultRow label={labels.totalCostPerUnit || 'Totale kosten per bestelling'} value={formatCurrency(result.totalCostPerUnit)} copyKey="totalCost" copied={copied} onCopy={copyToClipboard} bold copyLabel={labels.copyResult} />
            </div>
            <div className={`border-t border-slate-200 pt-3 ${result.profitPerUnit >= 0 ? '' : ''}`}>
              <ResultRow
                label={labels.profitPerUnit || 'Winst per bestelling'}
                value={formatCurrency(result.profitPerUnit)}
                copyKey="profit"
                copied={copied}
                onCopy={copyToClipboard}
                bold
                valueColor={result.profitPerUnit >= 0 ? 'text-green-600' : 'text-red-600'}
                copyLabel={labels.copyResult}
              />
              <div className="mt-1 flex items-center justify-between">
                <span className="text-xs text-slate-500">{labels.margin || 'Marge'}</span>
                <span className={`text-sm font-medium ${result.margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {result.margin.toFixed(1)}%
                </span>
              </div>
            </div>
            <div className="border-t border-slate-200 pt-3 space-y-2">
              <ResultRow label={labels.monthlyProfit || 'Maandelijkse winst'} value={formatCurrency(result.monthlyProfit)} copyKey="monthlyProfit" copied={copied} onCopy={copyToClipboard} valueColor={result.monthlyProfit >= 0 ? 'text-green-600' : 'text-red-600'} copyLabel={labels.copyResult} />
              <ResultRow label={labels.yearlyProfit || 'Jaarlijkse winst'} value={formatCurrency(result.yearlyProfit)} copyKey="yearlyProfit" copied={copied} onCopy={copyToClipboard} bold valueColor={result.yearlyProfit >= 0 ? 'text-green-600' : 'text-red-600'} copyLabel={labels.copyResult} />
            </div>
          </div>
        )}

        {/* Reset Button */}
        <button
          type="button"
          onClick={reset}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <RotateCcw className="h-4 w-4" />
          {labels.reset || 'Reset'}
        </button>
      </div>

      {/* Data Updated Notice */}
      <div className="mt-3 text-center text-xs text-slate-400">
        {labels.dataUpdated || 'Tarieven bijgewerkt'}: {BOL_DATA_UPDATED}
      </div>

      {/* Commission Rate Table */}
      <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">{labels.commissionTable || 'Bol.com commissietarieven'}</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="py-2 pr-4 text-left font-medium text-slate-600">{labels.category || 'Categorie'}</th>
                <th className="py-2 pl-4 text-right font-medium text-slate-600">{labels.commissionRate || 'Commissie'}</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(groupedCategories).map(([group, categories]) => (
                <Fragment key={group}>
                  <tr>
                    <td colSpan={2} className="pt-4 pb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">{group}</td>
                  </tr>
                  {categories.map((cat) => (
                    <tr key={cat.id} className="border-b border-slate-100 last:border-0">
                      <td className="py-2 pr-4 text-slate-700">{cat.label}</td>
                      <td className="py-2 pl-4 text-right font-medium text-slate-900">{(cat.rate * 100).toFixed(0)}%</td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Shipping Tiers Table */}
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">{labels.shippingTable || 'Bol.com verzendtarieven'}</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="py-2 pr-4 text-left font-medium text-slate-600">{labels.tier || 'Type'}</th>
                <th className="py-2 px-4 text-right font-medium text-slate-600">{labels.dimensions || 'Afmetingen'}</th>
                <th className="py-2 px-4 text-right font-medium text-slate-600">VVB</th>
                <th className="py-2 pl-4 text-right font-medium text-slate-600">{labels.fixedFee || 'Vaste bijdrage'}</th>
              </tr>
            </thead>
            <tbody>
              {BOL_SHIPPING_TIERS.map((tier) => (
                <tr key={tier.id} className="border-b border-slate-100 last:border-0">
                  <td className="py-2 pr-4 font-medium text-slate-700">{labels[`tier_${tier.id}`] || tier.id}</td>
                  <td className="py-2 px-4 text-right text-slate-600 text-xs">{tier.dimensions}</td>
                  <td className="py-2 px-4 text-right text-slate-900">{formatCurrency(tier.vvbCost)}</td>
                  <td className="py-2 pl-4 text-right text-slate-900">{formatCurrency(tier.fixedContribution)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Privacy Notice */}
      <div className="mt-6 rounded-xl bg-slate-50 p-4 text-center text-sm text-slate-600">
        {content.privacyNotice}
      </div>

      {/* Signup Wall */}
      <ToolSignupWall locale={locale} />

      {/* CTA Banner */}
      <div className="mt-8 rounded-2xl bg-gradient-deep p-6 text-white">
        <h2 className="text-xl font-semibold">{content.ctaTitle}</h2>
        <p className="mt-2 text-brand-100">{content.ctaDescription}</p>
        <Link
          href={`/${locale}/signup/`}
          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-brand-700 transition-all hover:bg-brand-50"
        >
          {content.ctaButton}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {/* Instructions */}
      {content.instructions && content.instructions.length > 0 && (
        <div className="mt-8">
          <h3 className="mb-4 text-lg font-semibold text-slate-900">
            {locale === 'nl' ? 'Hoe werkt het?' : locale === 'de' ? 'Wie funktioniert es?' : locale === 'fr' ? 'Comment ça marche?' : 'How does it work?'}
          </h3>
          <ol className="space-y-2">
            {content.instructions.map((instruction, index) => (
              <li key={index} className="flex gap-3 text-slate-700">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-medium text-brand-700">
                  {index + 1}
                </span>
                {instruction}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Disclaimer */}
      {content.disclaimer && (
        <div className="mt-8 border-t border-slate-200 pt-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">{content.disclaimer.heading}</h3>
          <p className="text-xs text-slate-500 leading-relaxed">{content.disclaimer.content}</p>
        </div>
      )}
    </div>
  );
}

function ResultRow({ label, value, copyKey, copied, onCopy, bold, valueColor, copyLabel }: {
  label: string;
  value: string;
  copyKey: string;
  copied: string | null;
  onCopy: (value: string, key: string) => void;
  bold?: boolean;
  valueColor?: string;
  copyLabel?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-sm ${bold ? 'font-medium text-slate-700' : 'text-slate-600'}`}>{label}</span>
      <div className="flex items-center gap-2">
        <span className={`${bold ? 'text-lg font-semibold' : 'font-medium'} ${valueColor || 'text-slate-900'}`}>{value}</span>
        <button
          onClick={() => onCopy(value.replace(/[^0-9,.-]/g, ''), copyKey)}
          className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
          title={copyLabel || 'Copy'}
        >
          {copied === copyKey ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

